import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CreateSubscription, Agent, Zone } from '../../../services/externalDataService.service';
import { SubscriptionService } from '../../../services/subscription.service';
import { AuthService } from '../../../services/auth.service';
import { LoadingComponent } from '../../shared/loading/loading.component';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-create-subscription',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LoadingComponent],
  templateUrl: './create-subscription.component.html',
  styleUrl: './create-subscription.component.css'
})
export class CreateSubscriptionComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  subscriptionForm: FormGroup;
  isLoading = false;
  isSubmitting = false;
  errorMessage = '';
  successMessage = '';

  // Observables from service
  agents$: Observable<Agent[]>;
  zones$: Observable<Zone[]>;
  loading$: Observable<boolean>;

  // Shared data from service
  planTypes: any[] = [];
  cadenceOptions: any[] = [];
  paymentMethods: any[] = [];

  constructor(
    private fb: FormBuilder,
    private subscriptionService: SubscriptionService,
    private authService: AuthService,
    private router: Router
  ) {
    this.subscriptionForm = this.createForm();
    this.agents$ = this.subscriptionService.agents$;
    this.zones$ = this.subscriptionService.zones$;
    this.loading$ = this.subscriptionService.loading$;
    
    // Initialize shared data
    this.planTypes = this.subscriptionService.getPlanTypes();
    this.cadenceOptions = this.subscriptionService.getCadenceOptions();
    this.paymentMethods = this.subscriptionService.getPaymentMethods();
  }

  ngOnInit(): void {
    // Initialize the service if not already done
    this.subscriptionService.initialize();
    
    // Clear error message when form values change
    this.subscriptionForm.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (this.errorMessage) {
        this.errorMessage = '';
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createForm(): FormGroup {
    const form = this.fb.group({
      agentId: ['', [Validators.required]],
      zoneId: ['', [Validators.required]],
      type: [1, [Validators.required]], // Default to Basic plan (1 = Basic)
      cadence: [1, [Validators.required]], // Default to Weekly (1 = Weekly)
      paymentMethod: [1, [Validators.required]], // Default to Cash (1)
      paymentReference: [''],
      notes: ['']
    });
    
    return form;
  }


  onSubmit(): void {
    if (this.subscriptionForm.valid) {
      this.isSubmitting = true;
      this.errorMessage = '';
      this.successMessage = '';

      // Wait for MSAL to be initialized before proceeding
      if (!this.authService.isMsalInitialized()) {
        this.isSubmitting = false;
        this.errorMessage = 'Authentication not ready. Please try again.';
        return;
      }

      const formData = this.subscriptionForm.value;
      const activeAccount = this.authService.getActiveAccount();
      
      // Try to get the email address from various MSAL account properties
      const idTokenClaims = (activeAccount as any)?.idTokenClaims;
      const emailFromClaims = idTokenClaims?.email || idTokenClaims?.preferred_username || idTokenClaims?.upn;
      
      const verifiedBy = activeAccount?.username || 
                        emailFromClaims ||
                        activeAccount?.localAccountId || 
                        activeAccount?.homeAccountId?.split('.')[0] || 
                        activeAccount?.name || 
                        'Unknown';
      
      // Additional check - if still Unknown, show error
      if (verifiedBy === 'Unknown') {
        this.isSubmitting = false;
        this.errorMessage = 'Unable to identify current user. Please refresh and try again.';
        return;
      }
      
      const subscriptionData: CreateSubscription = {
        agentId: formData.agentId,
        zoneId: formData.zoneId,
        type: Number(formData.type), // Convert to number (HTML select returns string)
        cadence: Number(formData.cadence), // Convert to number
        paymentMethod: Number(formData.paymentMethod), // Convert to number
        paymentReference: formData.paymentReference,
        notes: formData.notes,
        verifiedBy: verifiedBy
      };

      this.subscriptionService.createSubscription(subscriptionData).subscribe({
        next: () => {
          this.isSubmitting = false;
          this.successMessage = 'Subscription created successfully! The agent can now access their subscription.';
          // Reset form
          this.subscriptionForm.reset();
          this.subscriptionForm.patchValue({ 
            type: 1,
            cadence: 1,
            paymentMethod: 1
          });
        },
        error: (error) => {
          this.isSubmitting = false;
          console.error('Subscription creation error:', error);
          console.error('Error response:', error?.error);
          
          // Extract meaningful error message from API response
          if (error?.error?.error) {
            // API returns error in error.error.error format
            this.errorMessage = error.error.error;
          } else if (error?.error?.message) {
            // API returns error in error.error.message format
            this.errorMessage = error.error.message;
          } else if (error?.error && typeof error.error === 'string') {
            // API returns error as string in error.error
            this.errorMessage = error.error;
          } else if (error?.message) {
            // HTTP error message
            this.errorMessage = error.message;
          } else {
            // Fallback message
            this.errorMessage = 'Failed to create subscription. Please try again.';
          }
        }
      });
    } else {
      this.markFormGroupTouched();
    }
  }

  onCancel(): void {
    this.router.navigate(['/subscriptions']);
  }

  private markFormGroupTouched(): void {
    Object.keys(this.subscriptionForm.controls).forEach(key => {
      const control = this.subscriptionForm.get(key);
      control?.markAsTouched();
    });
  }

  // Helper methods for validation
  isFieldInvalid(fieldName: string): boolean {
    const field = this.subscriptionForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.subscriptionForm.get(fieldName);
    if (field && field.errors) {
      if (field.errors['required']) return `${fieldName} is required`;
      if (field.errors['min']) return `${fieldName} must be at least ${field.errors['min'].min}`;
    }
    return '';
  }

}
